/**
 * KAN-976: журнал операций кошелька с фильтрами по типу и аккаунту и страницами.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { WalletEntriesTable } from '@/components/wallet/WalletEntriesTable';
import { useWallet, useWalletEntries, useWalletEntriesFilters } from '@/hooks/useWallet';
import { isWalletEntryKind, walletKindFilterOptions } from '@/lib/wallet';

export function WalletEntriesSection() {
  const { kind, setKind, account, setAccount, page, setPage, params } = useWalletEntriesFilters();
  const { data, isLoading, isError } = useWalletEntries(params);
  const { data: wallet } = useWallet();
  const kindOptions = walletKindFilterOptions(wallet?.kinds);
  const entries = data?.results ?? [];

  return (
    <section className="rounded-lg bg-white p-4 shadow sm:p-6">
      <h3 className="mb-3 text-base font-semibold text-gray-900">Журнал операций</h3>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:max-w-2xl">
        <div className="space-y-1">
          <Label htmlFor="wallet-filter-kind">Тип</Label>
          <NativeSelect
            id="wallet-filter-kind"
            data-testid="wallet-filter-kind"
            value={kind}
            options={kindOptions}
            onChange={(e) => setKind(isWalletEntryKind(e.target.value) ? e.target.value : '')}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="wallet-filter-account">Аккаунт</Label>
          <Input
            id="wallet-filter-account"
            data-testid="wallet-filter-account"
            placeholder="ID аккаунта"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Загрузка...</p>}
      {isError && (
        <p className="text-sm text-red-600" role="alert">
          Не удалось загрузить журнал
        </p>
      )}
      {data && entries.length === 0 && (
        <p data-testid="wallet-entries-empty" className="py-6 text-center text-sm text-gray-500">
          Операций пока нет
        </p>
      )}
      {entries.length > 0 && <WalletEntriesTable entries={entries} />}

      {data && (data.next || data.previous) && (
        <div className="mt-4 flex items-center justify-between gap-2 text-sm text-gray-600">
          <span>
            Страница {page}, всего операций {data.count}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Предыдущая страница"
              data-testid="wallet-entries-prev"
              disabled={!data.previous}
              onClick={() => setPage(Math.max(1, page - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Следующая страница"
              data-testid="wallet-entries-next"
              disabled={!data.next}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
